(function () {
  'use strict';

  var root = null;
  var panel = null;
  var overlay = null;
  var closeBtn = null;
  var input = null;
  var revealEls = [];

  var lastFocusedEl = null;
  var scrollY = 0;

  var boundOverlayClick = null;
  var boundCloseClick = null;
  var boundPanelLeave = null;
  var boundPanelEnter = null;
  var hoverCloseTimer = null;
  var HOVER_CLOSE_DELAY = 180;

  var boundInput = null;
  var searchTimer = null;
  var searchController = null;
  var defaultMarkup = null;
  var SEARCH_DEBOUNCE = 250;

  function lockBodyScroll() {
    scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + scrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unlockBodyScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  function revealAll() {
    // The drawer is a fixed overlay, not a scrolling page section, so the
    // global IntersectionObserver-driven reveal system never fires here.
    // Add the visible state synchronously the moment the drawer opens.
    for (var i = 0; i < revealEls.length; i++) {
      revealEls[i].classList.add('oddit-is-visible');
    }
  }

  function getResultParts(scope) {
    return {
      popular: scope.querySelector('.oddit-search-drawer__popular'),
      suggested: scope.querySelector('.oddit-search-drawer__suggested'),
      products: scope.querySelector('.oddit-search-drawer__products')
    };
  }

  function snapshotDefault() {
    var parts = getResultParts(root);
    if (!parts.popular || !parts.suggested || !parts.products) return null;
    var head = parts.suggested.cloneNode(true);
    var track = head.querySelector('.oddit-search-drawer__products');
    if (track) track.parentNode.removeChild(track);
    return {
      popular: parts.popular.innerHTML,
      suggestedHead: head.innerHTML,
      products: parts.products.innerHTML
    };
  }

  function applyMarkup(markup) {
    var parts = getResultParts(root);
    if (!parts.popular || !parts.suggested || !parts.products) return;
    parts.popular.innerHTML = markup.popular;
    Array.prototype.slice.call(parts.suggested.childNodes).forEach(function (node) {
      if (node !== parts.products) parts.suggested.removeChild(node);
    });
    parts.products.insertAdjacentHTML('beforebegin', markup.suggestedHead);
    parts.products.innerHTML = markup.products;
    parts.products.scrollLeft = 0;
    revealEls = root.querySelectorAll('.oddit-reveal');
    revealAll();
  }

  function markupFromResponse(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var parts = getResultParts(doc);
    if (!parts.popular || !parts.suggested || !parts.products) return null;
    var products = parts.products.innerHTML;
    parts.products.parentNode.removeChild(parts.products);
    return {
      popular: parts.popular.innerHTML,
      suggestedHead: parts.suggested.innerHTML,
      products: products
    };
  }

  function runSearch(query) {
    if (searchController) searchController.abort();
    if (!query) {
      if (defaultMarkup) applyMarkup(defaultMarkup);
      return;
    }
    searchController = new AbortController();
    var types = (root.getAttribute('data-search-resource-types') || 'product') + ',query';
    var limit = root.getAttribute('data-search-results-limit') || '4';
    var url = (root.getAttribute('data-predictive-search-url') || '/search/suggest') +
      '?q=' + encodeURIComponent(query) +
      '&resources[type]=' + encodeURIComponent(types) +
      '&resources[limit]=' + encodeURIComponent(limit) +
      '&resources[options][unavailable_products]=last' +
      '&section_id=' + encodeURIComponent(root.getAttribute('data-section-id'));
    fetch(url, { signal: searchController.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('Predictive search failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        if (!input || input.value.trim() !== query) return;
        var markup = markupFromResponse(html);
        if (markup) applyMarkup(markup);
      })
      .catch(function (error) {
        if (error.name !== 'AbortError') console.error(error);
      });
  }

  function isOpen() {
    return !!root && root.classList.contains('is-open');
  }

  function open(triggerEl) {
    if (!root || isOpen()) return;
    lastFocusedEl = triggerEl || document.activeElement;
    root.style.top = '';
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    lockBodyScroll();
    revealAll();
    if (input) {
      input.focus();
    }
  }

  function close() {
    clearTimeout(hoverCloseTimer);
    if (!root || !isOpen()) return;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    unlockBodyScroll();
    if (lastFocusedEl && typeof lastFocusedEl.focus === 'function') {
      lastFocusedEl.focus();
    }
    lastFocusedEl = null;
  }

  function toggle(triggerEl) {
    if (isOpen()) {
      close();
    } else {
      open(triggerEl);
    }
  }

  function handleKeydown(event) {
    if (event.key === 'Escape' && isOpen()) {
      close();
    }
  }

  function bindEvents() {
    if (overlay) {
      boundOverlayClick = function () {
        close();
      };
      overlay.addEventListener('click', boundOverlayClick);
    }

    if (closeBtn) {
      boundCloseClick = function () {
        close();
      };
      closeBtn.addEventListener('click', boundCloseClick);
    }

    if (panel) {
      boundPanelLeave = function (event) {
        if (event.pointerType !== 'mouse' || !isOpen()) return;
        clearTimeout(hoverCloseTimer);
        hoverCloseTimer = setTimeout(close, HOVER_CLOSE_DELAY);
      };
      boundPanelEnter = function () {
        clearTimeout(hoverCloseTimer);
      };
      panel.addEventListener('pointerleave', boundPanelLeave);
      panel.addEventListener('pointerenter', boundPanelEnter);
    }

    if (input) {
      boundInput = function () {
        clearTimeout(searchTimer);
        var query = input.value.trim();
        searchTimer = setTimeout(function () {
          runSearch(query);
        }, SEARCH_DEBOUNCE);
      };
      input.addEventListener('input', boundInput);
    }

    document.addEventListener('keydown', handleKeydown);
  }

  function unbindEvents() {
    clearTimeout(searchTimer);
    if (searchController) searchController.abort();
    if (input && boundInput) {
      input.removeEventListener('input', boundInput);
    }
    boundInput = null;
    clearTimeout(hoverCloseTimer);
    if (panel && boundPanelLeave) {
      panel.removeEventListener('pointerleave', boundPanelLeave);
      panel.removeEventListener('pointerenter', boundPanelEnter);
    }
    boundPanelLeave = null;
    boundPanelEnter = null;
    if (overlay && boundOverlayClick) {
      overlay.removeEventListener('click', boundOverlayClick);
    }
    if (closeBtn && boundCloseClick) {
      closeBtn.removeEventListener('click', boundCloseClick);
    }
    document.removeEventListener('keydown', handleKeydown);
    boundOverlayClick = null;
    boundCloseClick = null;
  }

  function init() {
    // Select by class, not a hardcoded id — section.id is generated by
    // Shopify at render time and will never equal a literal string.
    root = document.querySelector('.oddit-search-drawer');
    if (!root) return;

    panel = root.querySelector('.oddit-search-drawer__panel');
    overlay = root.querySelector('[data-search-overlay]');
    closeBtn = root.querySelector('[data-search-close]');
    input = root.querySelector('[data-search-input]');
    revealEls = root.querySelectorAll('.oddit-reveal');
    defaultMarkup = snapshotDefault();

    bindEvents();
  }

  function teardown() {
    unbindEvents();
    root = null;
    panel = null;
    overlay = null;
    closeBtn = null;
    input = null;
    revealEls = [];
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor compatibility: re-bind when this section's markup is
  // swapped out after a settings change, and clean up on removal so we
  // don't leak listeners bound to detached nodes.
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target && event.target.querySelector && event.target.querySelector('.oddit-search-drawer')) {
      teardown();
      init();
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (root && event.target && event.target.contains && event.target.contains(root)) {
      teardown();
    }
  });

  // Typing fetches predictive results via the Section Rendering API
  // (/search/suggest?section_id=...); pressing enter still submits the form
  // natively to /search?q=... for the full results page.

  window.OdditSearchDrawer = {
    open: open,
    close: close,
    toggle: toggle,
    isOpen: isOpen
  };
})();
